import type { CSSProperties } from "react";
import { useEffect } from "react";
import { Outlet, ScrollRestoration, useLocation } from "react-router";
import { LoginSheet } from "@/features/auth";
import { useMergeGuestCartOnLogin } from "@/features/cart";
import { useMergeGuestFavoritesOnLogin } from "@/features/favorites";
import { cn } from "@/shared/lib/cn";
import { rememberTabLocation, useShell } from "../model/shell";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { MobileHomeHeader } from "./MobileHomeHeader";
import { NavProgress } from "./NavProgress";
import { TabBar } from "./TabBar";

/**
 * Every storefront and cabinet page. Desktop: header, page, footer. Phones: an app shell (docs/MOBILE_UI_PLAN.html
 * D1–D9) — the route's `handle.shell` picks the tab, the home header, the canvas background and whether the bottom
 * tab bar shows. Back/forward restore the scroll position; a new page starts at the top.
 */
export function ShopLayout() {
  const shell = useShell();
  const location = useLocation();
  const tabBar = shell.tabBar !== false;
  useMergeGuestCartOnLogin();
  useMergeGuestFavoritesOnLogin();

  // each tab remembers where the customer was (a filtered listing, a cabinet section)
  useEffect(() => {
    // without an open sheet: the tab must not reopen filters or the login later
    const params = new URLSearchParams(location.search);
    params.delete("sheet");
    const query = params.toString();
    rememberTabLocation(shell.tab, location.pathname + (query ? `?${query}` : ""));
  }, [shell.tab, location.pathname, location.search]);

  return (
    <div
      style={
        {
          "--tab-bar-h": tabBar ? "calc(3.5rem + env(safe-area-inset-bottom))" : "0px",
        } as CSSProperties
      }
      className={cn(
        "flex min-h-dvh flex-col",
        shell.canvas ? "bg-canvas lg:bg-surface" : "bg-surface",
        // room for the fixed tab bar at the end of the page (and of the footer)
        tabBar && "pb-(--tab-bar-h) lg:pb-0",
      )}
    >
      <ScrollRestoration getKey={(entry) => entry.key} />
      <NavProgress />
      <div className="max-lg:hidden">
        <Header />
      </div>
      {shell.homeHeader && <MobileHomeHeader />}
      <main className="flex-1">
        <Outlet />
      </main>
      <div className={cn(!shell.footer && "max-lg:hidden")}>
        <Footer />
      </div>
      {tabBar && <TabBar active={shell.tab} />}
      <LoginSheet />
    </div>
  );
}
